import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  // --- State ---
  private navigation: any;
  private isEdit: boolean = false;

  constructor(
    private router: Router,
    private storageService: StorageService
  ) {
    this.loadRoutes();
  }

  // --- Public Navigation Methods ---

  /**
   * Navigates to a specific route and updates the navigation history.
   * @param commands URL segments
   * @param tab Optional tab identifier (e.g., 'rainbow', 'cfg-wa')
   * @param extras Navigation extras
   */
  public navigate(commands: any[], tab?: string | null, extras?: NavigationExtras): Promise<void> {
    return new Promise(resolve => {
      const url = '/' + commands.join('/');
      const navItem = { commands, extras, url };

      if (tab) {
        // Private navigation (tab-specific)
        if (!this.navigation.private[tab]) {
          this.navigation.private[tab] = [];
        }
        // Add to history if not already the current top
        if (!this.navigation.private[tab].find((el: any) => el.url === url)) {
          this.navigation.private[tab].push(navItem);
        }
        // Clear public history when entering private tab
        this.navigation.public = [];
      } else {
        // Public navigation
        if (this.navigation.public && !this.navigation.public.find((el: any) => el.url === url)) {
          this.navigation.public.push(navItem);
          this.navigation.private = {};
        }
      }

      this.saveNavigation();
      this.router.navigate(commands, extras);
      resolve();
    });
  }

  /**
   * Navigates back to the previous route in the history.
   * @param tab Optional tab identifier
   */
  public previous(tab?: string): Promise<void> {
    return new Promise(resolve => {
      let destination: any = '';

      if (tab) {
        // Private navigation back
        if (this.navigation.private[tab].length > 1) {
          this.navigation.private[tab].pop();
        }
        destination = this.navigation.private[tab].slice(-1)[0];
      } else {
        // Public navigation back
        if (this.navigation.public.length > 1) {
          this.navigation.public.pop();
        }
        destination = this.navigation.public.slice(-1)[0];
      }

      this.saveNavigation();
      if (destination) {
        this.router.navigate(destination.commands, destination.extras);
      }
      resolve();
    });
  }

  /**
   * Complex logic to handle "Back" button behavior based on phase and context.
   * @param phaseCode The code of the current phase
   * @param previousUrl The URL we came from
   */
  public goToPrevius(phaseCode: string, previousUrl: string) {
    // 1. Always back to Production Orders if coming from there
    if (previousUrl === '/production-orders') {
      this.navigate(['production-orders'], 'rainbow');
      return;
    }

    // 2. Specific Phases: Rettifica (RE), Tempra (TE), Lucidatura (LU)
    if (['RE', 'TE', 'LU'].includes(phaseCode)) {
      if (previousUrl === '/worker-activities/summary' && this.isEdit) {
        this.isEdit = false;
        this.navigate(['worker-activities', 'summary'], 'cfg-wa');
        return;
      }
      
      if (['/worker-activities/process', '/production-orders'].includes(previousUrl)) {
        this.navigate(['production-orders'], 'rainbow', { replaceUrl: true });
        return;
      }

      this.previous('cfg-wa');
      return;
    }

    // 3. Specific Phases: Cromatura (CR), Collaudo (CO)
    if (['CR', 'CO'].includes(phaseCode)) {
      if (previousUrl === '/worker-activities/summary' && this.isEdit) {
        this.isEdit = false;
        this.navigate(['worker-activities', 'summary'], 'cfg-wa');
        return;
      }

      if (['/worker-activities/data', '/production-orders'].includes(previousUrl)) {
        this.navigate(['production-orders'], 'rainbow', { replaceUrl: true });
        return;
      }
    }

    // 4. Default fallback
    this.previous('cfg-wa');
  }

  // --- Tab Management ---

  public initializeTabNavigation(tab: string): Promise<void> {
    return new Promise(resolve => {
      if (!this.navigation.private[tab]) {
        this.navigation.private[tab] = [];
        const rootUrl = '/private/' + tab;
        
        if (!this.navigation.private[tab].find((el: any) => el.url === rootUrl)) {
          this.navigation.private[tab].push({ commands: ['private', tab], url: rootUrl });
        }
      }
      this.saveNavigation();
      resolve();
    });
  }

  public resetTabNavigation(tab: string): Promise<void> {
    return new Promise(resolve => {
      this.navigation.private[tab] = [];
      this.navigation.private[tab].push({ commands: ['private', tab], url: '/private/' + tab });
      
      this.saveNavigation();
      this.router.navigate(['/private/' + tab]);
      resolve();
    });
  }

  // --- State Setters ---

  public setIsEditable(value: boolean) {
    this.isEdit = value;
  }

  // --- Private Helpers ---

  private loadRoutes() {
    this.navigation = JSON.parse(localStorage.getItem('navigation'));
    if (!this.navigation) {
      this.navigation = { 'private': {}, 'public': [] };
    }
  }

  private saveNavigation() {
    this.storageService.set('navigation', JSON.stringify(this.navigation));
  }
}
